import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Tabs,
    Tab,
    Paper,
    Grid,
    Card,
    CardContent,
    Chip
} from '@mui/material';
import {
    Public as PublicIcon,
    LocationOn as LocationIcon,
    LocationCity as CityIcon
} from '@mui/icons-material';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import CountryManagement from '../../components/location/CountryManagement';
import StateManagement from '../../components/location/StateManagement';
import CityManagement from '../../components/location/CityManagement';
import locationService from '../../services/locationService';

function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`location-tabpanel-${index}`}
            aria-labelledby={`location-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function LocationManagement() {
    const [tabValue, setTabValue] = useState(0);
    const [stats, setStats] = useState(null);

    useEffect(() => {
        loadLocationStats();
    }, []);

    const loadLocationStats = async () => {
        try {
            const response = await locationService.getLocationStats();
            if (response.success) {
                setStats(response.data);
            }
        } catch (error) {
            console.error('Error loading location stats:', error);
        }
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const refreshStats = () => {
        loadLocationStats();
    };

    return (
        <DashboardLayout>
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h4" component="h1" gutterBottom>
                        Location Management
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Manage countries, states, and cities in the system
                    </Typography>
                </Box>

                {/* Statistics Cards */}
                {stats && (
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={4}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <PublicIcon color="primary" sx={{ mr: 1 }} />
                                        <Typography variant="h6">Countries</Typography>
                                    </Box>
                                    <Typography variant="h4" color="primary" gutterBottom>
                                        {stats.countries.total}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Chip 
                                            label={`${stats.countries.active} Active`} 
                                            color="success" 
                                            size="small" 
                                        />
                                        <Chip 
                                            label={`${stats.countries.inactive} Inactive`} 
                                            color="default" 
                                            size="small" 
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <LocationIcon color="secondary" sx={{ mr: 1 }} />
                                        <Typography variant="h6">States</Typography>
                                    </Box>
                                    <Typography variant="h4" color="secondary" gutterBottom>
                                        {stats.states.total}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Chip 
                                            label={`${stats.states.active} Active`} 
                                            color="success" 
                                            size="small" 
                                        />
                                        <Chip 
                                            label={`${stats.states.inactive} Inactive`} 
                                            color="default" 
                                            size="small" 
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <CityIcon color="info" sx={{ mr: 1 }} />
                                        <Typography variant="h6">Cities</Typography>
                                    </Box>
                                    <Typography variant="h4" color="info.main" gutterBottom>
                                        {stats.cities.total}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Chip 
                                            label={`${stats.cities.active} Active`} 
                                            color="success" 
                                            size="small" 
                                        />
                                        <Chip 
                                            label={`${stats.cities.inactive} Inactive`} 
                                            color="default" 
                                            size="small" 
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* Tabs */}
                <Paper sx={{ width: '100%' }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs 
                            value={tabValue} 
                            onChange={handleTabChange} 
                            aria-label="location management tabs"
                        >
                            <Tab 
                                icon={<PublicIcon />} 
                                label="Countries" 
                                id="location-tab-0"
                                aria-controls="location-tabpanel-0"
                            />
                            <Tab 
                                icon={<LocationIcon />} 
                                label="States" 
                                id="location-tab-1"
                                aria-controls="location-tabpanel-1"
                            />
                            <Tab 
                                icon={<CityIcon />} 
                                label="Cities" 
                                id="location-tab-2"
                                aria-controls="location-tabpanel-2"
                            />
                        </Tabs>
                    </Box>

                    <TabPanel value={tabValue} index={0}>
                        <CountryManagement onDataChange={refreshStats} />
                    </TabPanel>
                    <TabPanel value={tabValue} index={1}>
                        <StateManagement onDataChange={refreshStats} />
                    </TabPanel>
                    <TabPanel value={tabValue} index={2}>
                        <CityManagement onDataChange={refreshStats} />
                    </TabPanel>
                </Paper>
        </DashboardLayout>
    );
}

export default LocationManagement; 